import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { forgotPassword, verifyResetOtp, resetPassword, clearError, clearSuccessMessage } from "../../redux/slices/authSlice";
import { Link, useNavigate } from "react-router-dom";
import { Box, Typography, Paper, Alert, CircularProgress, IconButton, TextField, Button } from "@mui/material";
import ArrowBackIosOutlined from "@mui/icons-material/ArrowBackIosOutlined";

const ForgotPassword = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { loading, error, successMessage } = useSelector((state) => state.auth);

  const [step, setStep] = useState(1); // 1 = Enter Email, 2 = Enter OTP, 3 = Enter New Password
  const [email, setEmail] = useState("");
  const [otp, setOtp] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [validationError, setValidationError] = useState("");

  useEffect(() => {
    dispatch(clearError());
    dispatch(clearSuccessMessage());
    setValidationError("");
  }, [dispatch, step]);

  const handleRequestOtp = async (e) => {
    e.preventDefault();
    if (email) {
      const resultAction = await dispatch(forgotPassword({ email }));
      if (forgotPassword.fulfilled.match(resultAction)) {
        setStep(2);
      }
    }
  };

  const handleVerifyOtp = async (e) => {
    e.preventDefault();
    if (email && otp) {
      const resultAction = await dispatch(verifyResetOtp({ email, otp }));
      if (verifyResetOtp.fulfilled.match(resultAction)) {
        setStep(3);
      }
    }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    setValidationError("");

    if (newPassword !== confirmPassword) {
      return setValidationError("Passwords do not match");
    }
    if (newPassword.length < 8) {
      return setValidationError("Password must be at least 8 characters");
    }

    if (email && otp && newPassword) {
      const resultAction = await dispatch(resetPassword({ email, otp, newPassword }));
      if (resetPassword.fulfilled.match(resultAction)) {
        setStep(4);
        setTimeout(() => {
          navigate("/login");
        }, 2000);
      }
    }
  };

  const handleBack = () => {
    if (step > 1) {
      setStep(step - 1);
    } else {
      navigate("/login");
    }
  };

  return (
    <Box sx={{ minHeight: "100vh", background: "radial-gradient(ellipse at 15% 10%, rgba(255, 155, 128, 0.12) 0%, transparent 45%), radial-gradient(ellipse at 85% 85%, rgba(255, 140, 56, 0.08) 0%, transparent 45%), #FAFAFA", display: "flex", flexDirection: "column" }}>
      <Box sx={{ py: { xs: 2, sm: 2.5 }, px: { xs: 2.5, sm: 4 }, width: "100%" }}>
        <img src={require("../../assets/images/handpik.png")} alt="Handpik" width={150} height={30} style={{ cursor: "pointer", display: "block" }} onClick={() => navigate("/")} />
      </Box>

      <Box sx={{ flex: 1, display: "flex", alignItems: { xs: "flex-start", sm: "flex-start" }, justifyContent: "center", px: { xs: 2, sm: 3 }, pb: { xs: 4, sm: 6 }, pt: { xs: 1, sm: 3 } }}>
        <Box sx={{ width: "100%", maxWidth: { xs: "100%", sm: 480 }, position: "relative" }}>
          {step !== 4 && (
            <IconButton onClick={handleBack} sx={{ position: { xs: "static", sm: "absolute" }, left: { sm: -56 }, top: { sm: 6 }, mb: { xs: 2, sm: 0 }, backgroundColor: "rgba(255, 255, 255, 0.95)", backdropFilter: "blur(6px)", border: "1px solid rgba(0, 0, 0, 0.08)", boxShadow: "0 2px 10px rgba(0,0,0,0.05)", width: { xs: 42, sm: 40 }, height: { xs: 42, sm: 40 }, display: "flex", alignItems: "center", justifyContent: "center", "&:hover": { backgroundColor: "#fff", transform: "scale(1.05)", boxShadow: "0 4px 14px rgba(0,0,0,0.08)" }, transition: "all 0.2s ease" }}>
              <ArrowBackIosOutlined sx={{ fontSize: { xs: "18px", sm: "17px" }, color: "#1a1a1a", ml: "4px" }} />
            </IconButton>
          )}

          <Paper elevation={0} sx={{ width: "100%", borderRadius: { xs: "24px", sm: "28px" }, background: "rgba(255, 255, 255, 0.98)", backdropFilter: "blur(12px)", border: "1px solid rgba(0, 0, 0, 0.06)", boxShadow: { xs: "0 10px 35px -5px rgba(0, 0, 0, 0.07), 0 0 1px rgba(0,0,0,0.08)", sm: "0 20px 50px -10px rgba(0, 0, 0, 0.05), 0 0 1px rgba(0,0,0,0.08)" }, overflow: "hidden" }}>
            <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", pt: { xs: 4.5, sm: 5 }, pb: { xs: 3, sm: 3.5 }, px: { xs: 3, sm: 5 }, background: "linear-gradient(180deg, rgba(255, 155, 128, 0.06) 0%, transparent 100%)" }}>
              <Box sx={{ width: { xs: 70, sm: 68 }, height: { xs: 70, sm: 68 }, borderRadius: "22px", bgcolor: "rgba(255, 140, 56, 0.08)", border: "1px solid rgba(255, 140, 56, 0.15)", display: "flex", alignItems: "center", justifyContent: "center", mb: 2, boxShadow: "0 8px 24px rgba(255, 140, 56, 0.10)" }}>
                <img src={require("../../assets/images/login-handpik-logo_cropped.png")} alt="Handpik" style={{ width: 40, height: 40, objectFit: "contain" }} />
              </Box>
            </Box>

            <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", px: { xs: 3, sm: 5 }, pt: { xs: 2, sm: 3 }, pb: { xs: 4.5, sm: 5 } }}>
              <Typography component="h1" sx={{ fontFamily: "'Lora', serif", fontSize: { xs: "25px", sm: "28px" }, fontWeight: 600, color: "#1A1A1A", letterSpacing: "-0.02em", textAlign: "center", mb: 1.2 }}>
                {step === 1 && "Forgot Password"}
                {step === 2 && "Verify Code"}
                {step === 3 && "Reset Password"}
                {step === 4 && "Password Reset!"}
              </Typography>
              <Typography variant="body2" sx={{ color: "#71717A", fontSize: { xs: "14px", sm: "13.5px" }, textAlign: "center", lineHeight: 1.55, maxWidth: { xs: "100%", sm: "340px" }, mb: 3 }}>
                {step === 1 && "Enter your email address to receive a password reset code."}
                {step === 2 && `We've sent a 6-digit reset code to ${email}`}
                {step === 3 && "Create a new password for your account."}
                {step === 4 && "Redirecting you to login..."}
              </Typography>

              {(error || validationError) && <Alert severity="error" sx={{ width: "100%", maxWidth: "360px", mb: 2.5, borderRadius: "12px", fontSize: "13px" }}>{validationError || error}</Alert>}
              {successMessage && <Alert severity="success" sx={{ width: "100%", maxWidth: "360px", mb: 2.5, borderRadius: "12px", fontSize: "13px" }}>{successMessage}</Alert>}

              {step === 1 && (
                <Box component="form" onSubmit={handleRequestOtp} sx={{ width: "100%", maxWidth: { xs: "100%", sm: "360px" }, display: "flex", flexDirection: "column", gap: 2, mb: 1 }}>
                  <TextField label="Email Address" type="email" variant="outlined" fullWidth required value={email} onChange={(e) => setEmail(e.target.value)} disabled={loading} />
                  <Button type="submit" variant="contained" fullWidth disabled={loading || !email} sx={{ py: 1.5, bgcolor: "#FF8C38", color: "white", fontWeight: 600, textTransform: "none", fontSize: "15px", borderRadius: "12px", boxShadow: "0 4px 14px rgba(255, 140, 56, 0.25)", "&:hover": { bgcolor: "#e67a2a" } }}>
                    {loading ? <CircularProgress size={24} color="inherit" /> : "Send Reset Code"}
                  </Button>
                </Box>
              )}
              
              {step === 2 && (
                <Box component="form" onSubmit={handleVerifyOtp} sx={{ width: "100%", maxWidth: { xs: "100%", sm: "360px" }, display: "flex", flexDirection: "column", gap: 2, mb: 1 }}>
                  <TextField label="6-Digit OTP" variant="outlined" fullWidth required value={otp} onChange={(e) => setOtp(e.target.value)} disabled={loading} inputProps={{ maxLength: 6 }} />
                  <Button type="submit" variant="contained" fullWidth disabled={loading || otp.length !== 6} sx={{ py: 1.5, bgcolor: "#FF8C38", color: "white", fontWeight: 600, textTransform: "none", fontSize: "15px", borderRadius: "12px", boxShadow: "0 4px 14px rgba(255, 140, 56, 0.25)", "&:hover": { bgcolor: "#e67a2a" } }}>
                    {loading ? <CircularProgress size={24} color="inherit" /> : "Verify Code"}
                  </Button>
                  <Typography variant="caption" sx={{ color: "#A1A1AA", mt: 0.5, textAlign: "center", display: "block", alignSelf: "center", maxWidth: "280px" }}>
                    If you do not see the email in your inbox, please check your spam or junk folder.
                  </Typography>
                </Box>
              )}

              {step === 3 && (
                <Box component="form" onSubmit={handleResetPassword} sx={{ width: "100%", maxWidth: { xs: "100%", sm: "360px" }, display: "flex", flexDirection: "column", gap: 2, mb: 1 }}>
                  <TextField label="New Password" type="password" variant="outlined" fullWidth required value={newPassword} onChange={(e) => setNewPassword(e.target.value)} disabled={loading} inputProps={{ minLength: 8 }} />
                  <TextField label="Confirm New Password" type="password" variant="outlined" fullWidth required value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)} disabled={loading} inputProps={{ minLength: 8 }} />
                  <Button type="submit" variant="contained" fullWidth disabled={loading || newPassword.length < 8 || confirmPassword.length < 8} sx={{ py: 1.5, bgcolor: "#FF8C38", color: "white", fontWeight: 600, textTransform: "none", fontSize: "15px", borderRadius: "12px", boxShadow: "0 4px 14px rgba(255, 140, 56, 0.25)", "&:hover": { bgcolor: "#e67a2a" } }}>
                    {loading ? <CircularProgress size={24} color="inherit" /> : "Set New Password"}
                  </Button>
                </Box>
              )}
            </Box>
          </Paper>
        </Box>
      </Box>
    </Box>
  );
};

export default ForgotPassword;
